import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "portal_wapper" }

import { CacheMicroDataType, CacheMicroNumDataType } from '@/types/indexDB'
import { MicroApp } from '@/types'
import { ref, markRaw, onMounted, computed, watch, onUnmounted, nextTick } from 'vue'
import { indexDBStore, qiankunStore } from '@/store/index'
import globalApi from '@/utils/globalApi'
import { appStore } from '@/store/index'
import { storeToRefs } from 'pinia'
import { addErrorHandler, addGlobalUncaughtErrorHandler, registerMicroApps, RegistrableApp, start } from 'qiankun'
import router from '@/router'
import { getApps } from '@/api/app'
import { tokenVerify } from '@/api/login'
import { getCookie, getLocalstorage, getTokenExpireAt } from '@/utils/index'

const VERIFY_AHEAD = 2 * 60 * 1000 // 提前多久校验
const MAX_SLEEP = 10 * 60 * 1000 // 单次最长休眠
const FALLBACK_INTERVAL = 5 * 60 * 1000 // 解析不到 exp 时的回退间隔


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

// 1.scrollBehavior的作用？
// 2.为什么在beforeEach中使用await health()会多触发一次？
// 3.f2e-hwork-portal也会对dom进行操作 为什么他会显得很轻量

// Kiro
// 逐个回答。
// 1. scrollBehavior 的作用
// 控制路由切换后页面滚动位置。比如从列表页滚到底部，点进详情页，你希望详情页从顶部开始显示，就用 scrollBehavior: () => ({ top: 0 })。
// vue-router 内部实现是：导航完成后，调用 scrollBehavior 拿到返回值，然后执行 window.scrollTo()。如果返回的是 Promise，vue-router 会 await 这个 Promise，等它 resolve 后再滚动。
// 关键在于：vue-router 在 await Promise 的过程中，会额外触发一次 history.replaceState 来保存滚动位置到 history.state 里。这次 replaceState 被 qiankun 的路由劫持捕获到，qiankun 以为发生了路由变化，就又遍历所有微应用调用一遍 activeRule。
// 同步返回 { top: 0 } 就没有这个中间态，一步到位，不会多触发。
// 2. 为什么 await health() 会多触发一次
// vue-router 的导航流程是这样的：
// URL 变化 → qiankun 感知到，调用所有 activeRule（第 1 次）
//   → vue-router 开始导航
//     → beforeEach 执行
//       → await health()（异步等待）
//       → next() 调用
//     → 导航确认，vue-router 调用 history.replaceState 更新 state
//       → qiankun 再次感知到 replaceState，调用所有 activeRule（第 2 次）
// 如果 beforeEach 是同步的（没有 await），next() 立刻执行，导航确认和 URL 变化几乎在同一个微任务里完成，qiankun 只感知到一次变化。
// 但 await health() 把导航拆成了两个阶段：URL 先变了（第 1 次触发），等异步完成后导航才确认并更新 state（第 2 次触发）。中间有时间差，qiankun 就多感知了一次。
// f2e-hwork-portal 的 beforeEach 虽然代码很长，但已登录用户走的是这个分支：
// if (store.getters['id']) {
//   toPage(to, next, ...)  // 直接 next()，同步完成
// }
// 首次加载时确实有大量 await，但首次加载时微应用还没注册，activeRule 还不存在，所以不受影响。后续导航全部同步 next()，不会多触发。
// 3. f2e-hwork-portal 的 DOM 操作为什么显得轻量
// f2e-hwork-portal 的 DOM 操作（setCurrentAppName 里的缓存/挂载逻辑）确实也很重，但关键区别是：它的 DOM 操作在 activeRule 函数内部只做了一次判断调用：
// if (isMatch) {
//   setCurrentAppName(app.name, projectUrlList)  // 只在匹配时执行
// }
// return cached || isMatch  // 返回值是简单的布尔运算
// 而你的 activeRuleChange 里，每次调用都做了：
// matchUrl 判断
// inCacheArr 查询
// getCacheName 查询
// setMicroLimit 操作
// setMicroToMap 操作
// createDomContainer / replaceDomContainer DOM 操作
// 这些逻辑本身不是问题，问题是它被调用了 3 次（N 个微应用 × 3 = 大量重复执行）。f2e-hwork-portal 的 activeRule 也会被多次调用，但因为 scrollBehavior 同步 + beforeEach 后续同步，实际只触发 1 次，所以 DOM 操作只执行 1 次，自然显得轻量。

// 总结：不是 f2e-hwork-portal 的逻辑更轻，而是它的触发次数更少。
// import { ref, reactive, computed, watch, onBeforeMount, onMounted, onBeforeUnmount, nextTick, h, getCurrentInstance } from 'vue'

const useIndexDBStore = indexDBStore()
const useQiankunStore = qiankunStore()
const useAppStore = appStore()

// 当前微应用
const currentMicroApp = ref<MicroApp>()
// 当前路径
const currentLocation = ref('')
// 微应用列表数据
const microApps = ref<MicroApp[]>([])
// 保活最大数量
const cacheMaxNumber = ref(0)
// 当前保活应用
const cacheActiveAndMaxNum = ref<Record<string, number>>({})
// 当前保活应用
/** 使用map保证数据添加顺序 new Map()
 * new Map(): {
 *  micro-one: {
 *   dom: micro?.dom || void 0, // 缓存dom
     list:[] // 微应用路由数组
 *  }
 * }
 */
// 保活对象
const appMaps = new Map()
// 上一次微应用路径
let prevLocation = ''
// 当前微应用ctiveRule状态
const activeRuleMaps = new Map()
// 防止 afterMount 之后 activeRuleChange 再次设置 loading=true
let afterMountCalled = false

// 全局路由 防止相互影响
useAppStore.setGlobalHistory(router)

// const rewriteAddEventListener = (app) => {
//   // 每个微应用独立保存原始方法
//   if (!global[`__ORIGIN_ADD_EVENT_LISTENER__`]) {
//     global[`__ORIGIN_ADD_EVENT_LISTENER__`] = global.addEventListener
//   }
//   // 拦截子应用router注册的popstate处理函数
//   global.addEventListener = (eventType, handler, ...args) => {
//     if (eventType === 'popstate') {
//       // 收集子应用的popstate事件处理函数
//       // 调用原始的addEventListener注册事件处理函数
//       global[`__ORIGIN_ADD_EVENT_LISTENER__`](
//         'popstate',
//         (...args) => {
//           // 子应用为当前子应用时，才调用popstate事件处理函数
//           const url = getPurePathName(window.location)
//           if (currentMicroApp.value && currentMicroApp.value.name === app.name) {
//             // if (!getMapDom(app.name, url)) {
//             //   handler(...args)
//             // }
//           }
//         },
//         ...args
//       )
//     } else {
//       global[`__ORIGIN_ADD_EVENT_LISTENER__`](eventType, handler, ...args)
//     }
//   }
// }

addErrorHandler((event: any) => {
  if (event.message.includes('router')) {
    console.warn('路由事件处理警告:', event);
    return false; // 阻止路由错误冒泡
  }
})
// qiankun 生命周期钩子 - 微应用加载前
const beforeLoad = (app: RegistrableApp<any>) => {
  console.log(`[Portal] beforeLoad: ${app.name}`, new Date().toISOString())
  return Promise.resolve()
}
// qiankun 生命周期钩子 - 微应用挂载前
const beforeMount = (app: RegistrableApp<any>) => {
  console.log(`[Portal] beforeMount: ${app.name}`, new Date().toISOString())
  return Promise.resolve()
}
// qiankun 生命周期钩子 - 微应用挂载后
const afterMount = (app: RegistrableApp<any>) => {
  console.log(`[Portal] afterMount: ${app.name} → setLoading(false)`, new Date().toISOString())
  afterMountCalled = true
  useAppStore.setLoading(false)
  return Promise.resolve()
}
// qiankun 生命周期钩子 - 微应用卸载前
const beforeUnmount = (app: RegistrableApp<any>) => {
  console.log(`[Portal] beforeUnmount: ${app.name}`, new Date().toISOString())
  const root = document.querySelector('.qiankun-container')
  const targetId = `#qiankun-${app.name}-container`
  const domELe = root?.querySelector(targetId)
  if (domELe) {
    domELe.style.display = 'none'
    console.log(`[Portal] beforeUnmount: 隐藏容器 ${targetId}`)
  }
  return Promise.resolve()
}
// 全局错误捕捉
const globalErrors = () => {
  addGlobalUncaughtErrorHandler((event: any) => {
    const { message: msg } = event
    useAppStore.setLoading(false)
    if (msg && msg.includes('died in status LOADING_SOURCE_CODE')) {
      // console.error('微应用加载失败，请检查应用是否可运行')
    }
  })
}
// 初始化微应用数据
const initQiankun = async (microList = microApps.value) => {
  if (useIndexDBStore && useIndexDBStore.IndexDB?.isReady) {
    const latest = await useIndexDBStore.IndexDB?.queryLatestByCursor<CacheMicroDataType>?.('cacheMicro')
    const latestNum = await useIndexDBStore.IndexDB?.queryLatestByCursor<CacheMicroNumDataType>?.('cacheMicroNum')
    cacheActiveAndMaxNum.value = (latest && latest.activeArr) || microList.map(micro => micro.name).reduce((acc: any, cur) => { acc[cur] = 3; return acc }, {}) // 保活列表
    cacheMaxNumber.value = Number(latestNum?.maxNumber) >= 0 ? (latestNum?.maxNumber || 0) : 4// 保活最大值
    console.log('保活微应用列表', cacheActiveAndMaxNum.value, '保活微应用数量', cacheMaxNumber.value, '所有微应用', microList)
  }
}
// 更新 service worker 中的缓存应用配置
const updateServiceWorkerKeepAliveApps = (keepAliveApps: MicroApp[] = []) => {
  // 手动注入service-worker
  if ('serviceWorker' in navigator) {
    // 强制每次请求都去执行service-worker.js 触发service-worker中的接口
    navigator.serviceWorker.register(`/service-worker.js?v=${Date.now()}}`, { scope: '/', type: 'module' }).then(_ => {
      _.update()
      navigator.serviceWorker.ready.then((registration) => {
        console.log('[SW]  registration', registration)
        // 向等待中的 SW 发送消息
        if (registration.waiting) {
          console.log('[SW]  waiting', registration)
          registration.waiting.postMessage({
            type: 'UPDATE_KEEPALIVE_APPS',
            apps: keepAliveApps.map(app => ({
              envHost: app.entry,
              envPath: app.envPath
            }))
          })
          // registration.update()
        }
        // else if (registration.active) {
        //   registration.active.postMessage({
        //     type: 'UPDATE_skipWaiting'

        //   })
        // }
      }).catch((error) => {
        console.warn('ServiceWorker: Service Worker 未就绪:', error)
      })
    })
  }
}
// 获取微应用列表数据
const getMicroList = () => {
  return new Promise(reslove => {
    getApps().then(res => {
      const { code, data } = res
      if (code === 200) {
        // portal的微应用 线上环境
        // {
        //   ...
        //   name: 'micro-one',
        //   entry: 'https://micro.772431358.xyz/portal/micro-one/',  // 注意末尾斜杠 不加斜杠会导致打包后的微应用找不到js或css资源
        //   container: '#subapp-container',
        //   ...
        // }
        microApps.value = data.map((micro: MicroApp) => ({
          ...micro,
          envPath: micro.activeRule,
          activeRule: (location: Location) => activeRuleChange(location, micro, micro.activeRule + ''),
          props: {
            mainAppRouter: router,
            baseName: micro.baseName,
            base: micro.base,
            globalApi,
            getMicroAliveArr,
            ...micro.props
          }
        }))
        updateServiceWorkerKeepAliveApps(microApps.value)
        reslove(microApps.value)
      }
    })
      .catch(error => {
        console.error('There was an error!', error);
        reslove([])
      })
  })
}
// qiankun激活activeRule回调
const activeRuleChange = (location: Location, micro: MicroApp, activeRule: string) => {
  // const name = getMicroNameByUrl(location)
  const url = getPurePathName(location)
  // --------解决vue-router4 bug导致activeRuleChange多次重复调用问题 start----------
  if (currentLocation.value && currentLocation.value !== location.pathname) {
    currentLocation.value = location.pathname
    activeRuleMaps.clear()
    afterMountCalled = false
  }
  if (activeRuleMaps.has(micro.name)) {
    currentLocation.value = location.pathname
    return activeRuleMaps.get(micro.name)
  }
  // --------end  保持以下逻辑只执行一次----------
  const isActive = matchUrl(location, activeRule)
  const isMicroCache = inCacheArr(micro.name as string)
  const isPrevMicroCache = inCacheArr(currentMicroApp.value?.name as string || micro.name)
  console.log(`[Portal] activeRuleChange: ${micro.name} url="${url}" isActive=${isActive} isMicroCache=${isMicroCache} currentMicroApp=${currentMicroApp.value?.name}`, new Date().toISOString())
  const hasCache = getCacheName(micro.name)
  // 判断是否超出保活最大值
  if (hasCache) {
    setMicroLimit(micro, url)
  }
  // 只处理当前激活微应用的逻辑
  if (isActive) {
    // 只设置保活的微应用数据
    if (isMicroCache) {
      !hasCache && setCacheName(micro.name)
    }
    // 是否保活
    const isCache = getCache(micro.name)
    // 非相同微应用
    if ((!currentMicroApp.value || !activeRule.includes(currentMicroApp.value?.name))) {
      // 加载loading - 仅当 afterMount 尚未触发时才设置，防止 afterMount 后再次触发
      if (!isCache && !afterMountCalled) {
        useAppStore.setLoading(true)
      }
      // 清空tabs
      useAppStore.setTabItem([])
    }
    // 当前应用为保活应用
    // 设置当前应用到map中
    setMicroToMap(micro, url, isMicroCache, isPrevMicroCache)
    if (isMicroCache) {
      if (isCache) {
        replaceDomContainer(micro, isCache)
        useAppStore.setLoading(false)
      } else createDomContainer(micro)
    } else {
      createDomContainer(micro)
    }
    prevLocation = getPurePathName(location)
  }
  // 是否当前激活路径 是否微应用保活
  const result = !!(isActive || getCacheName(micro.name))
  activeRuleMaps.set(micro.name, result)
  // 修复：主逻辑路径执行完毕后同步 currentLocation，防止后续 reroute 无法正确清除缓存
  currentLocation.value = location.pathname
  return result
}
// 微应用注册
const register = (appList: MicroApp[]) => {
  registerMicroApps(appList, {
    beforeLoad: [(app: any): Promise<void> => beforeLoad(app)],
    beforeMount: [(app: any): Promise<void> => beforeMount(app)],
    afterMount: [(app: any): Promise<void> => afterMount(app)],
    beforeUnmount: [(app: any): Promise<void> => beforeUnmount(app)]
  })
  start({
    prefetch: false,
    // 关闭单实例模式
    singular: false,
    sandbox: {
      // 阻止主应用路由事件传播到微应用
      // strictStyleIsolation: true,
      experimentalStyleIsolation: true
    }
  })
  globalErrors()
}
// 替换保活dom
const replaceDomContainer = (micro: MicroApp, dom: HTMLElement) => {
  // 删除前一个保活应用
  removeDomContainer()
  const root = document.querySelector('.qiankun-container')
  dom.style.display = 'block'
  root && root.appendChild(dom)
  currentMicroApp.value = micro
}
// 创建dom
const createDomContainer = (microApp: MicroApp) => {
  // removeDomContainer()
  const dom = document.querySelector('.qiankun-container')
  if (dom) {
    const domId = `qiankun-${microApp.name}-container`
    const currentDom = dom.querySelector(`#${domId}`) as HTMLElement
    if (currentDom) {
      currentDom.style.display = 'block'
      useAppStore.setLoading(false)
      return
      // removeDomContainer(`#${domId}`)
    }
    // 旧容器的清理已移至 beforeUnmount 生命周期钩子中处理，
    // 避免在 qiankun 卸载旧应用之前过早移除其 DOM 容器
    const appContainer = document.createElement('div')
    appContainer.id = domId
    appContainer.classList.add('qiankun_wapper')
    dom.appendChild(appContainer)
    currentMicroApp.value = microApp
  }
}
// 删除dom
const removeDomContainer = (id = '') => {
  const root = document.querySelector('.qiankun-container')
  if (currentMicroApp.value && currentMicroApp.value.name) {
    // 修复：createDomContainer 创建的容器 ID 格式为 `qiankun-${name}-container`，
    // 而非 API 配置中的 container 字段（如 #subapp-container），
    // 因此直接使用内部 ID 格式而非 getDomId() 来确保正确匹配。
    const targetId = id || `#qiankun-${currentMicroApp.value.name}-container`
    const domELe = root?.querySelector(targetId)
    domELe && root?.removeChild(domELe)
  }
}
// 设置当前应用到map中
const setMicroToMap = (micro: MicroApp, url: string, isMicroCache: boolean, isPrevMicroCache: boolean) => {
  // 初次加载
  if (!currentMicroApp.value?.name && isMicroCache) {
    setCacheName(micro.name)
    setCache(micro.name, url)
  // 保存上一个微应用的dom 判断当前微应用是否超出最大保活数
  } else if (getCacheName(currentMicroApp.value?.name || '') && isPrevMicroCache) {
    if (currentMicroApp.value && currentMicroApp.value && currentMicroApp.value.name) {
      // 修复：使用与 createDomContainer 一致的内部 ID 格式，而非 API 配置中的 container 字段
      const dom = document.querySelector(`#qiankun-${currentMicroApp.value.name}-container`) as HTMLElement
      dom.style.display = 'none'
      // setCacheName(currentMicroApp.value.name)
      setCache(currentMicroApp.value.name, '', dom)
      // 存储当前微应用的dom和cache url
      if (!getCache(micro.name, url) && isMicroCache) setCache(micro.name, url, undefined)
    }
  }
}

// 设置当前应用到map中
const setMicroLimit = (micro: MicroApp, url: string) => {
  // 保存当前url
  setCache(micro.name, url)
  if (getCacheName(currentMicroApp.value?.name || '')) {
    // 判断上一个保活应用是否超出最大保活数
    overMaxLimit(currentMicroApp?.value?.name as string)
  }
  // 处理当前微应用
  if (getCacheName(micro.name)) {
    overMaxLimit(micro.name)
  }
}
// 判断超出最大保活数
const overMaxLimit = (name: string) => {
  const activeDomCacheList = getCache(name, 'list')
  // 获取当前微应用最大缓存数
  const maxNum = cacheActiveAndMaxNum.value?.[name] || 0
  // 删除超出的路由页面
  if (activeDomCacheList.length > maxNum) {
    // const [_, ...args] = activeDomCacheList
    // setCache(name, [...args])
    activeDomCacheList.shift()
  }
  // 处理微应用数据
  if (appMaps.size > cacheMaxNumber.value) {
    deletCacheName(getFirstMapItemKey())
  }
}
// 匹配当前url地址
const matchUrl = (location: Location, activeRule: string) => {
  const isMatch = location.pathname.startsWith(activeRule)
  return isMatch
}
// 对保活map进行赋值
const getCacheName = (name: string) => {
  return appMaps.get(name) || false
}
// 设置保活map
const setCacheName = (name: string) => {
  appMaps.set(name, {
    dom: null,
    list: []
  })
}
// 删除保活map
const deletCacheName = (name = '') => {
  name && (appMaps.delete(name))
}
// 获取保活map第一个对象
const getFirstMapItemKey = () => {
  return appMaps.entries().next().value?.[0] || null
}
// 设置宝或微应用路由信息
const setCache = (name: string, urls = '', dom : undefined | HTMLElement = void 0) => {
  const mciroMap = getCacheName(name)
  if (mciroMap) {
    dom && mciroMap && (mciroMap.dom = dom)
    // 设置当前激活的路由集合
    if (Array.isArray(urls) && urls.length) {
      mciroMap.list = urls
    } else if (urls) {
      const temp = [...mciroMap.list]
      const index = temp.indexOf(urls)
      if (index > -1) temp.splice(index, 1) // 删除元素
      temp.push(urls) // 添加到末尾
      mciroMap.list = temp
    }
  }
}
// 获取宝或微应用路由信息
const getCache = (name: string, type = 'dom') => {
  if (type === 'dom') {
    return getCacheName(name)?.dom || false
  }
  return getCacheName(name).list
}
// 是否存在于保活列表中
const inCacheArr = (name: string) => {
  return !!cacheActiveAndMaxNum.value[name] || false
}
// 获取当前跳转微应用路由名
const getMicroNameByUrl = (location: Location) => {
  return location.pathname.split('/')[2]
}
// 获取当前跳转路由 纯净的
const getPurePathName = (location: Location) => {
  return location.pathname.split('?')[0]
}
// 根据子应用name获取子应用dom id
const getDomId = (name: string | null = null) => {
  return name ? microApps.value.find(micro => micro.name === name)?.container : null
}

// 获取微应用当前保活列表
const getMicroAliveArr = (micro: MicroApp, router: any) => {
  const pathArr = micro ? getCache(micro.name, 'list') : []
  const mappingArr = globalApi.pathToName(micro.baseName || micro.name, router)
  const res = mappingArr.filter(map => pathArr?.includes(map.path)).map(item => item.name)
  return res
}
// 登录态校验调度
// token 有效期长达 25 小时（access-expire-time: 1500 分），固定 30s 轮询一天要发 2880 次
// 请求去探测一个几乎不变的状态。这里改成读 JWT 的 exp 自适应调度：
//   过期前 2 分钟内 -> 立即校验（中间件会用 refreshToken 自动续期）
//   否则等到“过期前 2 分钟”那一刻再校验，单次最长等 10 分钟（兼须休眠/改时间场景）
//   exp 解析不出来时回退到 5 分钟定时
let healthTimer: ReturnType<typeof setTimeout> | null = null

const scheduleNextVerify = () => {
  if (healthTimer) {
    clearTimeout(healthTimer)
    healthTimer = null
  }

  const token = getCookie('x-token') || getLocalstorage('x-token')
  const expireAt = getTokenExpireAt(token)

  let delay = FALLBACK_INTERVAL
  if (expireAt) {
    // 距“应该校验的时间点”还有多久；已经过期或即将过期则为 0
    const untilVerify = expireAt - Date.now() - VERIFY_AHEAD
    delay = Math.min(Math.max(untilVerify, 0), MAX_SLEEP)
  }

  healthTimer = setTimeout(runVerify, delay)
}

const runVerify = async () => {
  // 登录页不查，否则会在自己身上触发一次 401 跳转
  if (!location.href?.includes('/register')) {
    const token = getCookie('x-token') || getLocalstorage('x-token')
    // 没 token 就不请求；该拦的路由守卫会拦
    if (token) {
      try {
        await tokenVerify()
      } catch {
        // 网络抖动不处理，下一轮继续；401 由 request.ts 拦截器统一跳转
      }
    }
  }
  scheduleNextVerify()
}

const startHealthCheck = () => {
  // 首次进入先校一次，确认手里的 token 真的有效
  runVerify()
}

// 页面从后台切回时立即补一次校验：
// setTimeout 在标签页休眠时不可靠，睡久了醒来 token 可能已经过期
const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    runVerify()
  }
}

onMounted(async () => {
  const list = await getMicroList() as MicroApp[]
  useQiankunStore.setMicroApps(list)
  await initQiankun()
  // 修复：在 register（即 start()）之前同步 currentLocation，确保 activeRuleChange 首次执行时状态正确
  currentLocation.value = window.location.pathname
  // 等待 Vue 完成 DOM 渲染周期，确保 .qiankun-container 等关键元素已挂载
  await nextTick()
  register(microApps.value)
  startHealthCheck()
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  if (healthTimer) {
    clearTimeout(healthTimer)
    healthTimer = null
  }
})


return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!
  const _component_a_config_provider = _resolveComponent("a-config-provider")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_a_config_provider, { "prefix-cls": "ant" }, {
      default: _withCtx(() => [
        _createVNode(_component_router_view)
      ]),
      _: 1
    })
  ]))
}
}

})